import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LOGO_URL, rupiah } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "long", timeStyle: "short" }) : "-");

export default function VerifyBill() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/verify-bill/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ valid: false }))
      .finally(() => setLoading(false));
  }, [id]);

  const paid = data && data.status === "paid";
  const partial = data && data.status !== "paid" && (data.paid_amount || 0) > 0;
  const unpaid = data && data.valid && !paid && !partial;

  const state = paid
    ? { bg: "bg-emerald-600", label: "LUNAS", note: "Struk terverifikasi asli", Icon: CheckCircle2 }
    : partial
    ? { bg: "bg-amber-500", label: "DIBAYAR SEBAGIAN", note: "Struk terverifikasi asli", Icon: CheckCircle2 }
    : { bg: "bg-rose-500", label: "BELUM DIBAYAR", note: "Tagihan ini belum dibayar", Icon: XCircle };

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col items-center justify-center p-5">
      <div className="w-full max-w-md" data-testid="verify-bill-page">
        <div className="text-center mb-5">
          <img src={LOGO_URL} alt="Al-Khoir" className="w-16 h-16 object-contain mx-auto mb-2" />
          <h1 className="font-heading font-bold text-lg text-stone-900">Pondok Pesantren Al-Khoir</h1>
          <p className="text-xs text-stone-500 flex items-center justify-center gap-1"><ShieldCheck size={13} /> Verifikasi Keaslian Struk</p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          ) : !data || !data.valid ? (
            <div className="p-8 text-center" data-testid="verify-invalid">
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
                <XCircle className="text-rose-600" size={34} />
              </div>
              <p className="font-heading font-bold text-lg text-rose-700">Struk Tidak Valid</p>
              <p className="text-sm text-stone-500 mt-1">Kode struk tidak ditemukan dalam sistem. Struk ini mungkin tidak asli.</p>
            </div>
          ) : (
            <div data-testid="verify-valid">
              <div className={`p-6 text-center text-white ${state.bg}`}>
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                  <state.Icon size={38} />
                </div>
                <p className="font-heading font-bold text-xl">{state.label}</p>
                <p className="text-sm opacity-90 mt-0.5">{state.note}</p>
              </div>
              <div className="p-6 space-y-3 text-sm">
                <Row label="Nama Santri" value={data.student} />
                <Row label="Tagihan" value={data.label} />
                <Row label="Jumlah Tagihan" value={rupiah(data.total_amount)} mono />
                <Row label="Dibayar" value={rupiah(data.paid_amount)} mono strong={!unpaid} />
                {!unpaid && <Row label="Tanggal Bayar" value={fmt(data.paid_at)} />}
                {data.manual_by && <Row label="Metode" value={`Manual (${data.manual_by})`} />}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-stone-400 mt-4">
          Halaman ini menampilkan data resmi dari sistem Al-Khoir. Untuk memverifikasi, pindai QR pada struk pembayaran.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, mono, strong }) {
  return (
    <div className="flex justify-between gap-4 border-b border-dashed border-stone-200 pb-2 last:border-0">
      <span className="text-stone-500">{label}</span>
      <span className={`text-right ${mono ? "font-mono" : ""} ${strong ? "font-bold text-emerald-700" : "font-medium text-stone-800"}`}>{value}</span>
    </div>
  );
}
